import React, { useState, useEffect } from 'react';
import apiClient from '../services/apiClient';
import { toast } from 'react-toastify';
import { Button } from '../components/ui';

interface AffiliateConfig {
  commission_percentage: string;
  cpa_reward_usd: string;
  cpa_threshold_usd: string;
  payout_mode: string;
  is_enabled: boolean;
}

const AffiliateSettings: React.FC = () => {
  const [config, setConfig] = useState<AffiliateConfig>({
    commission_percentage: '10.0',
    cpa_reward_usd: '10.0',
    cpa_threshold_usd: '50.0',
    payout_mode: 'same_currency',
    is_enabled: true,
  });

  const [loading, setLoading] = useState(false);
  const [fetching, setFetching] = useState(true);

  useEffect(() => {
    fetchConfig();
  }, []);

  const fetchConfig = async () => {
    try {
      const response = await apiClient.get('/admin/affiliate-config');
      if (response.data && response.data.success && response.data.data) {
        const d = response.data.data;
        setConfig({
          commission_percentage: String(d.commission_percentage || '10.0'),
          cpa_reward_usd: String(d.cpa_reward_usd || '10.0'),
          cpa_threshold_usd: String(d.cpa_threshold_usd || '50.0'),
          payout_mode: d.payout_mode || 'same_currency',
          is_enabled: d.is_enabled !== false,
        });
      }
    } catch (error) {
      console.error('Failed to fetch affiliate config:', error);
      toast.error('Failed to load affiliate settings.');
    } finally {
      setFetching(false);
    }
  };

  const handleInputChange = (e: React.ChangeEvent<HTMLInputElement | HTMLSelectElement>) => {
    const { name, value } = e.target;
    setConfig((prev) => ({
      ...prev,
      [name]: value,
    }));
  };

  const handleToggleChange = (e: React.ChangeEvent<HTMLInputElement>) => {
    const { name, checked } = e.target;
    setConfig((prev) => ({
      ...prev,
      [name]: checked,
    }));
  };

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    setLoading(true);

    try {
      const response = await apiClient.post('/admin/update-affiliate-config', config);
      if (response.data && response.data.success) {
        toast.success(response.data.message || 'Affiliate settings updated successfully!');
      } else {
        toast.error(response.data.message || 'Failed to update settings.');
      }
    } catch (error: any) {
      console.error('Failed to update affiliate config:', error);
      toast.error(error.response?.data?.message || 'Failed to update affiliate config.');
    } finally {
      setLoading(false);
    }
  };

  if (fetching) {
    return (
      <div className="ml-0 lg:ml-[256px] p-6 text-white text-center">
        <div className="text-lg font-semibold animate-pulse">Loading Affiliate Settings...</div>
      </div>
    );
  }

  return (
    <div className="ml-0 lg:ml-[256px] p-4 sm:p-6 text-white min-h-screen bg-[#151929]">
      <div className="bg-slate-900 border border-slate-800 rounded-xl p-4 sm:p-6 shadow-2xl">
        <h2 className="text-xl sm:text-2xl font-bold mb-6 border-b border-slate-800 pb-3">Global Affiliate Settings</h2>
        
        <form onSubmit={handleSubmit} className="space-y-6">
          <div className="flex items-center justify-between bg-slate-800/40 p-4 rounded-lg border border-slate-800/80">
            <div>
              <label htmlFor="is_enabled" className="block text-sm font-semibold text-gray-200">Enable Affiliate System</label>
              <span className="text-xs text-gray-400">Globally turn on or off the referral system</span>
            </div>
            <label className="relative inline-flex items-center cursor-pointer">
              <input
                type="checkbox"
                id="is_enabled"
                name="is_enabled"
                checked={config.is_enabled}
                onChange={handleToggleChange}
                className="sr-only peer"
              />
              <div className="w-11 h-6 bg-slate-700 peer-focus:outline-none rounded-full peer peer-checked:after:translate-x-full peer-checked:after:border-white after:content-[''] after:absolute after:top-[2px] after:left-[2px] after:bg-white after:border-gray-300 after:border after:rounded-full after:h-5 after:w-5 after:transition-all peer-checked:bg-indigo-500"></div>
            </label>
          </div>

          <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
            <div>
              <label htmlFor="commission_percentage" className="block text-sm font-medium text-gray-300 mb-2">
                Referral Commission Rate (%)
              </label>
              <div className="relative">
                <input
                  type="text"
                  id="commission_percentage"
                  name="commission_percentage"
                  value={config.commission_percentage}
                  onChange={handleInputChange}
                  placeholder="10.0"
                  className="w-full bg-slate-950 border border-slate-800 rounded-md px-3 py-2 text-sm text-white focus:border-indigo-500 focus:ring-1 focus:ring-indigo-500/20 focus:outline-none transition-all"
                />
                <span className="absolute right-3 top-2 text-gray-400 text-sm">%</span>
              </div>
              <p className="text-xs text-gray-500 mt-1">Percentage of wager/bet turnover paid to referrer</p>
            </div>

            <div>
              <label htmlFor="payout_mode" className="block text-sm font-medium text-gray-300 mb-2">
                Commission Payout Mode
              </label>
              <select
                id="payout_mode"
                name="payout_mode"
                value={config.payout_mode}
                onChange={handleInputChange}
                className="w-full bg-slate-950 border border-slate-800 rounded-md px-3 py-2 text-sm text-white focus:border-indigo-500 focus:outline-none transition-all"
              >
                <option value="same_currency">Same Currency (User played coin)</option>
                <option value="usdt_base">USDT Base (Auto convert to USDT)</option>
              </select>
              <p className="text-xs text-gray-500 mt-1">Currency in which the referrer gets credited</p>
            </div>
          </div>

          <div className="border-t border-slate-800 pt-6">
            <h3 className="text-lg font-semibold text-indigo-400 mb-4">CPA Bonus Configurations (One-time)</h3>
            
            <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
              <div>
                <label htmlFor="cpa_threshold_usd" className="block text-sm font-medium text-gray-300 mb-2">
                  CPA Qualification Deposit Target ($ USD)
                </label>
                <div className="relative">
                  <span className="absolute left-3 top-2.5 text-gray-400 text-xs">$</span>
                  <input
                    type="text"
                    id="cpa_threshold_usd"
                    name="cpa_threshold_usd"
                    value={config.cpa_threshold_usd}
                    onChange={handleInputChange}
                    placeholder="50.0"
                    className="w-full bg-slate-950 border border-slate-800 rounded-md pl-7 pr-3 py-2 text-sm text-white focus:border-indigo-500 focus:ring-1 focus:ring-indigo-500/20 focus:outline-none transition-all"
                  />
                </div>
                <p className="text-xs text-gray-500 mt-1">Minimum first deposit value (converted to USD) required to trigger CPA</p>
              </div>

              <div>
                <label htmlFor="cpa_reward_usd" className="block text-sm font-medium text-gray-300 mb-2">
                  CPA Referral Reward Value ($ USD)
                </label>
                <div className="relative">
                  <span className="absolute left-3 top-2.5 text-gray-400 text-xs">$</span>
                  <input
                    type="text"
                    id="cpa_reward_usd"
                    name="cpa_reward_usd"
                    value={config.cpa_reward_usd}
                    onChange={handleInputChange}
                    placeholder="10.0"
                    className="w-full bg-slate-950 border border-slate-800 rounded-md pl-7 pr-3 py-2 text-sm text-white focus:border-indigo-500 focus:ring-1 focus:ring-indigo-500/20 focus:outline-none transition-all"
                  />
                </div>
                <p className="text-xs text-gray-500 mt-1">One-time reward paid to referrer when referred player makes their first qualifying deposit</p>
              </div>
            </div>
          </div>

          <div className="pt-4 flex justify-end">
            <Button variant="primary" type="submit" disabled={loading} className="px-6 py-2.5 bg-indigo-600 text-white font-bold hover:bg-indigo-500 disabled:bg-slate-700 transition-all rounded-md">
              {loading ? 'Saving Settings...' : 'Save Settings'}
            </Button>
          </div>
        </form>
      </div>
    </div>
  );
};

export default AffiliateSettings;
